import React, { useEffect, useState } from 'react';
import { useAuth } from '../App';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, ReferenceLine } from 'recharts';
import { TrendUp, Trophy, Medal, ArrowUp, ArrowDown, Equals } from '@phosphor-icons/react';
import { format, parseISO } from 'date-fns';
import { de, enUS } from 'date-fns/locale';

/**
 * Admin/staff/trainer view of another user's Padlr Rating + Level progression.
 * Fetches from GET /admin/users/{userId}/rating-history and renders a compact
 * KPI bar, chart and recent-history list. Mirrors the customer-facing
 * PadlrRating component but keeps the endpoint and data-testid separate.
 */
const AdminRatingHistoryPanel = ({ userId }) => {
  const { API, language } = useAuth();
  const L = (en, deu) => (language === 'de' ? deu : en);
  const dateLocale = language === 'de' ? de : enUS;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancel = false;
    if (!userId) return;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const { data } = await API.get(`/admin/users/${userId}/rating-history`);
        if (!cancel) setData(data);
      } catch (e) {
        if (!cancel) setError(e.response?.data?.detail || 'Failed to load');
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [API, userId]);

  if (loading) return <div className="bg-white border border-slate-200/60 rounded-2xl p-6 animate-pulse h-48" />;
  if (error) return <div className="bg-white border border-slate-200/60 rounded-2xl p-6 text-sm text-rose-600">{error}</div>;
  if (!data) return null;

  const chartData = (data.history || [])
    .filter(h => typeof h.rating_after === 'number')
    .map(h => ({
      date: h.applied_at,
      rating: h.rating_after,
      label: h.event_title || (h.kind === 'match' ? L('Match', 'Spiel') : L('Event', 'Event')),
    }));
  const firstWithBefore = (data.history || []).find(h => typeof h.rating_before === 'number');
  if (chartData.length > 0 && firstWithBefore) {
    const firstDate = chartData[0].date ? new Date(chartData[0].date) : new Date();
    firstDate.setSeconds(firstDate.getSeconds() - 60);
    chartData.unshift({
      date: firstDate.toISOString(),
      rating: firstWithBefore.rating_before,
      label: L('Start', 'Start'),
    });
  }
  if (chartData.length > 0 && chartData[chartData.length - 1].rating !== data.padlr_rating) {
    chartData.push({ date: new Date().toISOString(), rating: data.padlr_rating, label: 'Now' });
  }

  const lastEntries = (data.history || []).slice().reverse().slice(0, 20);
  const minRating = chartData.length ? Math.min(...chartData.map(d => d.rating)) - 30 : 1400;
  const maxRating = chartData.length ? Math.max(...chartData.map(d => d.rating)) + 30 : 1600;

  return (
    <div className="space-y-4" data-testid="admin-rating-history-panel">
      {/* KPI bar */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-200/60 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-1">
            <Trophy size={14} weight="fill" className="text-emerald-600" />
            <span className="text-[11px] font-bold uppercase tracking-wider text-emerald-700">{L('Padlr Rating', 'Padlr Rating')}</span>
          </div>
          <p className="text-3xl font-black text-emerald-700" data-testid="admin-rating-value">{data.padlr_rating}</p>
          <span className="text-[10px] text-emerald-700/80">{L('Derived from level (800–2800)', 'Aus dem Level berechnet (800–2800)')}</span>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400">{L('Player level', 'Spielstärke')}</span>
          <p className="text-3xl font-black text-slate-900 mt-1">{data.player_level?.toFixed(2)}</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400">{L('Matches', 'Spiele')}</span>
          <p className="text-3xl font-black text-slate-900 mt-1">{data.matches_played}</p>
          <span className="text-[11px] text-slate-500">{data.matches_won} {L('won', 'gewonnen')}</span>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400">{L('Win rate', 'Siegquote')}</span>
          <p className="text-3xl font-black text-slate-900 mt-1">{data.win_rate}%</p>
        </div>
      </div>

      {/* Chart */}
      <div className="bg-white border border-slate-200/60 rounded-2xl p-5 shadow-sm">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <TrendUp size={18} className="text-emerald-600" weight="duotone" />
            <h3 className="font-bold text-slate-900">{L('Rating progression', 'Rating-Entwicklung')}</h3>
          </div>
          <span className="text-[11px] text-slate-400">{L('All time', 'Alle Zeit')}</span>
        </div>
        {chartData.length === 0 ? (
          <p className="text-center text-slate-400 py-12 text-sm">
            {L('No rated matches yet.', 'Noch keine bewerteten Spiele.')}
          </p>
        ) : (
          <div style={{ width: '100%', height: 220 }}>
            <ResponsiveContainer>
              <LineChart data={chartData} margin={{ top: 6, right: 8, left: -16, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                <XAxis
                  dataKey="date"
                  tickFormatter={(d) => { try { return format(parseISO(d), 'd. MMM', { locale: dateLocale }); } catch { return d; } }}
                  stroke="#94a3b8"
                  tick={{ fontSize: 11 }}
                />
                <YAxis domain={[minRating, maxRating]} stroke="#94a3b8" tick={{ fontSize: 11 }} />
                <Tooltip
                  contentStyle={{ background: '#fff', border: '1px solid #e2e8f0', fontSize: 12, borderRadius: 8 }}
                  labelFormatter={(d) => { try { return format(parseISO(d), 'PP', { locale: dateLocale }); } catch { return d; } }}
                  formatter={(v) => [v, L('Rating', 'Rating')]}
                />
                <ReferenceLine y={1500} stroke="#cbd5e1" strokeDasharray="4 4" label={{ value: L('start', 'Start'), position: 'right', fontSize: 10, fill: '#94a3b8' }} />
                <Line type="monotone" dataKey="rating" stroke="#10B981" strokeWidth={2.5} dot={{ fill: '#10B981', r: 3 }} activeDot={{ r: 5 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* Recent history */}
      <div className="bg-white border border-slate-200/60 rounded-2xl p-5 shadow-sm">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Medal size={18} className="text-amber-500" weight="duotone" />
            <h3 className="font-bold text-slate-900">{L('Full history', 'Vollständiger Verlauf')}</h3>
          </div>
          <span className="text-[11px] text-slate-400">{(data.history || []).length} {L('entries', 'Einträge')}</span>
        </div>
        {lastEntries.length === 0 ? (
          <p className="text-center text-slate-400 py-8 text-sm">{L('No results yet', 'Noch keine Resultate')}</p>
        ) : (
          <div className="divide-y divide-slate-100">
            {lastEntries.map((h, idx) => {
              const isMatch = h.kind === 'match';
              const isLegacy = h.kind === 'mixin_legacy';
              const change = h.change || 0;
              return (
                <div key={idx} className="py-2.5 flex items-center justify-between" data-testid={`admin-rating-history-${idx}`}>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-slate-900 truncate">
                      {isMatch
                        ? `${L('Competitive match', 'Kompetitives Spiel')} · ${h.court_name || ''}`
                        : (h.event_title || L('Mixin', 'Mixin'))}
                      {isLegacy && <span className="ml-2 text-[10px] text-slate-400">({L('legacy', 'älter')})</span>}
                    </p>
                    <p className="text-[11px] text-slate-400">
                      {h.applied_at ? (() => { try { return format(parseISO(h.applied_at), 'PP', { locale: dateLocale }); } catch { return ''; } })() : ''}
                      {isMatch && (
                        <span className={`ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold ${h.won ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'}`}>
                          {h.won ? L('Won', 'Sieg') : L('Lost', 'Niederlage')}
                        </span>
                      )}
                      {!isMatch && typeof h.start_court === 'number' && typeof h.final_court === 'number' && (
                        <span className="ml-2 text-slate-400">
                          {L('Court', 'Court')} {h.start_court} → {h.final_court}
                        </span>
                      )}
                      {!isMatch && typeof h.is_winner === 'boolean' && (
                        <span className={`ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold ${h.is_winner ? 'bg-amber-50 text-amber-700' : 'bg-slate-100 text-slate-500'}`} data-testid={`admin-mixin-result-${idx}`}>
                          {h.is_winner ? L('🏆 Winner', '🏆 Turniersieg') : L('No win', 'Kein Sieg')}
                        </span>
                      )}
                      {typeof h.rating_before === 'number' && typeof h.rating_after === 'number' && (
                        <span className="ml-2 text-slate-500">
                          {h.rating_before} → {h.rating_after}
                        </span>
                      )}
                      {typeof h.level_before === 'number' && typeof h.level_after === 'number' && h.level_before !== h.level_after && (
                        <span className="ml-2 text-slate-500 font-semibold">
                          L{h.level_before.toFixed(2)} → L{h.level_after.toFixed(2)}
                        </span>
                      )}
                    </p>
                  </div>
                  <div className={`flex items-center gap-1 text-sm font-bold ${
                    change > 0 ? 'text-emerald-600' : change < 0 ? 'text-rose-600' : 'text-slate-400'
                  }`}>
                    {change > 0 && <ArrowUp size={14} weight="bold" />}
                    {change < 0 && <ArrowDown size={14} weight="bold" />}
                    {change === 0 && <Equals size={14} weight="bold" />}
                    {change > 0 ? `+${change}` : change}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminRatingHistoryPanel;
